/**
 * JSON-LD 序列化与取值归一化的纯函数层。
 *
 * 三个函数各自守住一条不变量，供 `src/seo/jsonld/*` 的构造器、
 * 运行时 `src/utils/seo.ts` 的 `injectJsonLd()` 与构建期
 * `scripts/prerender/serialize.ts` 共用：
 *
 * - `serializeJsonLd()`：输出不含裸 `</script`，且能被标准 JSON 解析器解析回等价对象
 *   （需求 3.7、4.17）
 * - `toAbsoluteUrl()`：`url` / `item` / `@id` / `target` 恒为 WWW_Domain 绝对地址且幂等
 *   （需求 4.13）
 * - `clampJsonLdText()`：`name` / `headline` / `description` 恒为去标签纯文本且长度落在给定闭区间
 *   （需求 4.15）
 *
 * 全部为纯函数：不读 DOM、不发网络请求、不读全局状态，Node 与浏览器结果一致。
 * 文本流水线（去标签 → 解码实体 → 折叠空白）复用 `src/seo/visibleText.ts`，
 * 让 JSON-LD 文本口径与 Visible_Text_Length 口径共用同一份实现。
 *
 * 对应需求：3.7、4.13、4.15、4.17
 */

import { PAGE_TYPE_META_PRESET, SITE_ORIGIN } from "../constants";
import type { PageType } from "../types";
import { extractVisibleText } from "../visibleText";

/** 站点首页绝对地址，`toAbsoluteUrl()` 在无可用路径时的返回值 */
const HOME_URL = `${SITE_ORIGIN}/`;

/**
 * `serializeJsonLd()` 的转义表。
 *
 * 只转这三个字符即可让 `<script type="application/ld+json">` 不被提前闭合：
 * `<` 被转义后 `</script`、`<!--` 之类的序列都不可能在输出中出现，
 * `>` 与 `&` 一并转义是为了同一段文本在被嵌入 HTML 属性或注释上下文时也安全。
 * `\uXXXX` 是 JSON 规范内的合法转义，因此转义后的文本仍可被标准解析器解析回等价值。
 */
const JSON_ESCAPES: Readonly<Record<string, string>> = {
  "<": "\\u003c",
  ">": "\\u003e",
  "&": "\\u0026",
};

const JSON_ESCAPE_PATTERN = /[<>&]/g;

/** 已是 WWW_Domain 绝对地址时，`SITE_ORIGIN` 之后允许出现的分隔符 */
const ORIGIN_BOUNDARY_CHARS = ["/", "?", "#"] as const;

/** http / https 之外的 scheme（`mailto:` 等）不做协议解析，按站内路径兜底处理 */
const HTTP_SCHEME_PATTERN = /^https?:\/\//i;

/** 结构化数据文本兜底补足语料，`PAGE_TYPE_META_PRESET` 之外的最后一层来源 */
const GENERIC_PAD_TEXT =
  "欧格登智能科技提供信息安全、IT基础架构、云信达与AI业务四大领域的企业级解决方案、产品选型、落地实施与持续运维服务，覆盖等保合规、零信任、数据中心与容灾备份等典型场景。";

/** 长度仍不足下限时逐字符补齐用的填充符，非空白字符，保证结果不以空白结尾 */
const FILLER_CHAR = "。";

/** 句读字符集合：拼接补足语料时用于判断是否需要补一个分隔符 */
const SENTENCE_ENDINGS = new Set([
  "。",
  "，",
  "、",
  "；",
  "：",
  "！",
  "？",
  ".",
  ",",
  ";",
  ":",
  "!",
  "?",
]);

/**
 * 解码实体后可能重新出现的标签形态（如源文本写的是 `&lt;p&gt;`）。
 *
 * `extractVisibleText()` 的流水线是「去标签 → 解码实体」，解码在后，
 * 所以被转义过一次的标签会在解码后现形。JSON-LD 的 `name` / `description`
 * 要求是纯文本，这里再扫一遍把这类残留去掉。
 */
const RESIDUAL_TAG_PATTERN = /<\/?[a-zA-Z][^<>]*>/g;

/** `clampJsonLdText()` 的补足语料来源 */
export type ClampJsonLdTextOptions = {
  /** 取 `PAGE_TYPE_META_PRESET[pageType]` 的兜底文案作为补足语料（需求 4.15） */
  pageType?: PageType;
  /** 替换预设模板中 `{name}` 占位符的名称，缺失时用该 Page_Type 的面包屑标签 */
  name?: string;
  /** 调用方自备的补足语料，优先级高于 Page_Type 预设（如列表页可传分类简介） */
  padSources?: readonly string[];
};

/**
 * 把任意 JSON 值序列化为可安全内嵌 `<script type="application/ld+json">` 的文本。
 *
 * 与直接 `JSON.stringify()` 的差别只有一处：`<` / `>` / `&` 被替换成
 * `\u003c` / `\u003e` / `\u0026`。这两点因此同时成立（需求 3.7、4.17）：
 * - 输出中不可能出现裸的 `</script`，脚本元素不会被正文提前闭合
 * - `\uXXXX` 是 JSON 合法转义，`JSON.parse(serializeJsonLd(v))` 与 `v` 深度相等
 *
 * 运行时用 `script.text = ...` 赋值本身是安全的（浏览器不把内容当 HTML 解析），
 * 但预渲染要把 DOM 用 `outerHTML` 序列化落盘，那条路径必须靠本函数兜住。
 *
 * @param value 任意可 JSON 序列化的值
 * @param space 缩进，默认紧凑输出（产物体积优先）
 * @returns JSON 文本；`value` 为 `undefined` 或函数等不可序列化值时返回 `"null"`，
 *          保证返回值恒可被 JSON 解析
 */
export function serializeJsonLd(value: unknown, space?: number | string): string {
  const json = JSON.stringify(value, undefined, space);

  // JSON.stringify(undefined) 返回 undefined：输出一个合法的 JSON 字面量兜底
  if (json === undefined) {
    return "null";
  }

  return json.replace(JSON_ESCAPE_PATTERN, (char) => JSON_ESCAPES[char]);
}

/** 输入是否已是 WWW_Domain 绝对地址（`https://www.ogolden.comfoo` 不算） */
function isCanonicalOrigin(url: string): boolean {
  if (!url.startsWith(SITE_ORIGIN)) {
    return false;
  }
  if (url.length === SITE_ORIGIN.length) {
    return true;
  }
  return ORIGIN_BOUNDARY_CHARS.includes(url[SITE_ORIGIN.length] as (typeof ORIGIN_BOUNDARY_CHARS)[number]);
}

/** `new URL()` 的安全包装：解析失败返回 `null` 而不抛异常 */
function tryParseUrl(input: string, base?: string): URL | null {
  try {
    return new URL(input, base);
  } catch {
    return null;
  }
}

/**
 * 把站内路径或任意 URL 归一化为 WWW_Domain 绝对地址（需求 4.13）。
 *
 * 处理规则：
 * - 已是 `https://www.ogolden.com` 前缀的绝对地址 → 原样返回（因此函数幂等）
 * - 站内路径（`/solutions`、`solutions/1`、`./a`、`#top`）→ 相对 `https://www.ogolden.com/` 解析
 * - 其他来源的 http/https 绝对地址（含 apex 域、http 协议、大写主机名）→
 *   保留 path / query / fragment，换成 WWW_Domain。JSON-LD 的 `url` / `item` / `@id`
 *   必须自指本站，跨站地址在这里被收敛而不是原样透出
 * - `mailto:` 之类非 http scheme 与解析失败的输入 → 按站内路径兜底，
 *   最终仍返回带 WWW_Domain 前缀的地址
 * - 空串、纯空白与非字符串 → 返回首页地址 `https://www.ogolden.com/`
 *
 * 返回值恒以 `https://www.ogolden.com` 开头，且 `toAbsoluteUrl(toAbsoluteUrl(x))`
 * 与 `toAbsoluteUrl(x)` 相等。
 */
export function toAbsoluteUrl(pathOrUrl: unknown): string {
  if (typeof pathOrUrl !== "string") {
    return HOME_URL;
  }

  const trimmed = pathOrUrl.trim();
  if (trimmed === "") {
    return HOME_URL;
  }

  // 幂等出口：已经是规范域名的绝对地址，逐字符原样返回
  if (isCanonicalOrigin(trimmed)) {
    return trimmed;
  }

  // 协议相对地址 `//host/path`：补 https 后按绝对地址处理
  const candidate = trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;

  if (HTTP_SCHEME_PATTERN.test(candidate)) {
    const parsed = tryParseUrl(candidate);
    if (parsed) {
      return `${SITE_ORIGIN}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  }

  // 其余一切（站内路径、非 http scheme、畸形输入）都按站内路径解析
  const relative = tryParseUrl(trimmed, HOME_URL);
  if (relative && relative.origin === SITE_ORIGIN) {
    return `${SITE_ORIGIN}${relative.pathname}${relative.search}${relative.hash}`;
  }

  // `mailto:x` 这类带 scheme 的输入会被 `new URL` 当成绝对地址（origin 为 null），
  // 再包一层显式路径前缀，保证返回值仍是 WWW_Domain 绝对地址
  const escaped = tryParseUrl(`/${trimmed.replace(/^\/+/, "")}`, HOME_URL);
  if (escaped) {
    return `${SITE_ORIGIN}${escaped.pathname}${escaped.search}${escaped.hash}`;
  }

  return HOME_URL;
}

/**
 * 把富文本 / HTML 片段清成 JSON-LD 可用的纯文本。
 *
 * 流水线：去 `script`/`style`/`noscript` 与全部标签 → 解码实体 → 折叠空白 → 去首尾空白
 * （复用 `extractVisibleText()`），随后再清一遍解码后现形的残留标签。
 */
function toPlainText(raw: unknown): string {
  if (typeof raw !== "string" || raw === "") {
    return "";
  }

  const visible = extractVisibleText(raw);
  if (visible.indexOf("<") < 0) {
    return visible;
  }

  return extractVisibleText(visible.replace(RESIDUAL_TAG_PATTERN, ""));
}

/** 按 code point 切分：避免代理对（表情符号等）被截断成半个字符 */
function toCodePoints(text: string): string[] {
  return Array.from(text);
}

function isWhitespace(char: string): boolean {
  return /\s/.test(char);
}

/** 去掉尾部空白 code point，使结果不以空白结尾（否则调用方 trim 后会跌破下限） */
function trimTrailingWhitespace(chars: string[]): void {
  while (chars.length > 0 && isWhitespace(chars[chars.length - 1])) {
    chars.pop();
  }
}

/**
 * 按优先级收集补足语料：调用方自备 → Page_Type 预设 → 通用兜底。
 *
 * 每条语料都先过 `toPlainText()`，保证补足进去的内容同样不含 HTML 标签。
 */
function collectPadSources(options: ClampJsonLdTextOptions): string[] {
  const sources: string[] = [];

  for (const source of options.padSources ?? []) {
    const text = toPlainText(source);
    if (text !== "") {
      sources.push(text);
    }
  }

  if (options.pageType) {
    const preset = PAGE_TYPE_META_PRESET[options.pageType];
    const name = toPlainText(options.name ?? "") || preset.breadcrumbLabel;

    sources.push(toPlainText(preset.descriptionTemplate.split("{name}").join(name)));
    sources.push(toPlainText(preset.fallbackTitle));
    for (const seed of preset.keywordSeeds) {
      const text = toPlainText(seed);
      if (text !== "") {
        sources.push(text);
      }
    }
  }

  sources.push(GENERIC_PAD_TEXT);

  return sources.filter((source) => source !== "");
}

/**
 * 用补足语料把 `chars` 填到不短于 `lower`。
 *
 * 语料循环取用，每轮至少追加 1 个 code point，因此循环次数不超过 `lower`，
 * 不存在无法终止的路径。
 */
function padToLowerBound(chars: string[], lower: number, sources: string[]): void {
  let index = 0;

  while (chars.length < lower && sources.length > 0) {
    const source = sources[index % sources.length];
    index += 1;

    if (chars.length > 0 && !SENTENCE_ENDINGS.has(chars[chars.length - 1])) {
      chars.push("，");
    }
    chars.push(...toCodePoints(source));
  }
}

/**
 * 把结构化数据的 `name` / `headline` / `description` 归一化到给定长度闭区间（需求 4.15）。
 *
 * 处理顺序与需求逐句对应：
 * 1. 去 HTML 标签、解码实体、折叠空白、去首尾空白（复用 Visible_Text_Length 的流水线）
 * 2. 超过 `max` 时按 code point 截断至 `max`
 * 3. 不足 `min` 时用 Page_Type 预设（或调用方自备语料）补足
 *
 * 返回值恒满足：长度（code point 数）落在 `[min, max]` 闭区间、不含 HTML 标签、
 * 不以空白开头或结尾（因此调用方再 `trim()` 也不会跌破下限）。
 *
 * `min > max` 属于调用方笔误，此时以 `max` 为准（返回长度为 `max`），
 * 保证函数在任何输入下都有确定的返回值而不抛异常。
 *
 * @param raw 源字段文本，可为富文本 / HTML 片段 / 空值
 * @param min 长度下限，如 `name` 用 4、`description` 用 40
 * @param max 长度上限，如 `name` 用 110、`description` 用 320
 * @param options 补足语料来源（Page_Type 预设与名称占位符）
 */
export function clampJsonLdText(
  raw: unknown,
  min: number,
  max: number,
  options: ClampJsonLdTextOptions = {},
): string {
  const upper = Number.isFinite(max) ? Math.max(0, Math.floor(max)) : Number.MAX_SAFE_INTEGER;
  const lower = Math.min(Number.isFinite(min) ? Math.max(0, Math.floor(min)) : 0, upper);

  const chars = toCodePoints(toPlainText(raw));

  if (chars.length > upper) {
    chars.length = upper;
    trimTrailingWhitespace(chars);
  }

  if (chars.length < lower) {
    padToLowerBound(chars, lower, collectPadSources(options));

    if (chars.length > upper) {
      chars.length = upper;
    }
    trimTrailingWhitespace(chars);

    // 截断点落在空白上、或补足语料全部为空时的最后一道保证
    while (chars.length < lower) {
      chars.push(FILLER_CHAR);
    }
  }

  return chars.join("");
}
